import { HTMLAttributes } from 'react';
import { useEffect, useState } from 'react';
import Components from '@/website/components/NewLandingPages';
import PartnerStatement from '@/website/components/NewLandingPages/components/partnerStatement/index';
import CommonProductIntroductionView from '@/website/components/NewLandingPages/components/productIntroduction/common';
import SupportCountriesAcrossEuropeView from '@/website/components/NewLandingPages/components/supportCountriesAcrossEurope/common';
import WidgetEmbed from '@/website/components/WidgetEmbed';
import GetQuoteSuspendedButton from '@/website/components/buttons/getQuoteSuspendedButton';
import QuickCostCalculatorView from '@/website/components/switchWithJove/quickCostCalculator/index';
import { LocaleEnum, useLocaleContext } from '@/website/contexts/AppContext';
import { useQuoteReleatedQuote } from '@/website/hooks/useQuoteReleatedQuote';
import { ContentfulPageProps } from '@/website/lib/getContentfulData';
import { InsuranceLandingPageEnum } from '@/website/types/insurerType';
import {
  EuropeanUnionList,
  PROVIDER_SOURCE,
  businessInsuranceKey,
  getBusinessInsuranceLandingPageThemeMap,
  judgeDisplayBusinessPromotionCustomLandingPage,
  judgeDisplayPromotionDefaultLandingPage,
  judgeDisplayWebsiteLandingTestimonialSection,
  judgeIsActiveBrokerAndPartnerPromotion,
  useJoveBreakpoint,
} from '@jove-frontend/uikit';
import { Box } from '@mui/material';
import clsx from 'clsx';

type BusinessInsuranceLandingPageViewProps = HTMLAttributes<HTMLDivElement> & ContentfulPageProps;
const BusinessInsuranceLandingPageView = (props: BusinessInsuranceLandingPageViewProps) => {
  const { post, promotion, className } = props;
  const [isWidgetOpen, setIsWidgetOpen] = useState(false);
  const { jumpToQuotePageAction } = useQuoteReleatedQuote();
  const { country } = useLocaleContext();
  const [isActiveBusinessPromotion, setIsActiveBusinessPromotion] = useState(false);
  const { isMobileLayout } = useJoveBreakpoint();

  /** Promotion logic */
  const hasActivePromotion = promotion?.status === 'active';
  const insuranceType = new Map([
    ['goods-in-transit-insurance', 'goods-in-transit'],
    ['business-insurance', 'business-insurance'],
    ['construction-insurance', 'hired-in-plant'],
  ]);
  const queryParams = new URLSearchParams();
  if (hasActivePromotion) {
    queryParams.set('utm_source', 'jove-website');
    queryParams.set('utm_medium', 'promotion');
    queryParams.set('utm_campaign', promotion.slug);
    queryParams.set('promotion_id', promotion.id);
    queryParams.set('display_insurance', insuranceType.get(promotion.insurance_type));
    queryParams.set('providerCode', promotion.insurance_providers[0].code);
  }

  const quoteSrc = hasActivePromotion ? `${process.env.NX_QUOTE_ENDPOINT}/?${queryParams.toString()}` : `${process.env.NX_QUOTE_ENDPOINT}`;
  /** End of promotion logic */

  /**
   * effects
   */
  useEffect(() => {
    setIsActiveBusinessPromotion(judgeIsActiveBrokerAndPartnerPromotion(promotion));
  }, [promotion]);

  /**
   * methods
   */
  const openQuotePageAction = () => {
    jumpToQuotePageAction();
  };

  const baseImgUrl = 'https://storage.googleapis.com/jove-public-images/website/landing-pages/business-insurance';

  return (
    <Box className={clsx('flex w-full flex-col', isMobileLayout ? 'px-2' : '', className)}>
      <PartnerStatement />
      {hasActivePromotion ? (
        judgeDisplayBusinessPromotionCustomLandingPage(promotion, {
          insuranceType: businessInsuranceKey,
          productScheme: getBusinessInsuranceLandingPageThemeMap().sme.value,
        }) ? (
          <CommonProductIntroductionView
            className="mb-16"
            insuranceType={businessInsuranceKey}
            productScheme={getBusinessInsuranceLandingPageThemeMap().sme.value}
          />
        ) : judgeDisplayPromotionDefaultLandingPage(promotion, {
            insuranceType: businessInsuranceKey,
            productScheme: getBusinessInsuranceLandingPageThemeMap().sme.value,
          }) ? (
          <Components.Hero
            post={post}
            setIsWidgetOpen={setIsWidgetOpen}
            bgImage={`${baseImgUrl}/hero-bg.png`}
            className="mb-16"
            insuranceType={businessInsuranceKey}
            productScheme={getBusinessInsuranceLandingPageThemeMap().sme.value}
          />
        ) : (
          <Components.PromotionHero
            promotion={promotion}
            imgFallback={`${baseImgUrl}/hero-bg.png`}
            // onClick={() => setIsWidgetOpen(true)}
            onClick={openQuotePageAction}
            post={post}
            className="mb-16"
          />
        )
      ) : (
        <Components.Hero post={post} setIsWidgetOpen={setIsWidgetOpen} bgImage={`${baseImgUrl}/hero-bg.png`} className="mb-16" />
      )}
      {!hasActivePromotion && <QuickCostCalculatorView providerCode={PROVIDER_SOURCE().ACCELERANT.value} />}
      {EuropeanUnionList.includes(country) && (
        <SupportCountriesAcrossEuropeView insuranceType={InsuranceLandingPageEnum.businessInsurance} info={post as any} />
      )}
      <Components.ProblemSolution post={post} problemImg={`${baseImgUrl}/problem-image.png`} solutionImg={`${baseImgUrl}/solution-image.png`} />
      {!isActiveBusinessPromotion && <Components.SeeInAction post={post} setIsWidgetOpen={setIsWidgetOpen} />}
      {!isActiveBusinessPromotion && <Components.WhyJove post={post} />}
      <Components.OurProducts post={post} />
      {!isActiveBusinessPromotion && <Components.SwitchToJove post={post} />}
      {judgeDisplayWebsiteLandingTestimonialSection(promotion) && (
        <Components.Testimonial imgSrc={`${baseImgUrl}/business-owner-image.png`} post={post} />
      )}
      {!isActiveBusinessPromotion && <Components.Referral post={post} promotion={promotion} />}
      {!isActiveBusinessPromotion && <Components.Explore post={post} />}
      {!isActiveBusinessPromotion && <Components.FAQ post={post} />}
      {isActiveBusinessPromotion && <GetQuoteSuspendedButton />}
      <WidgetEmbed isOpen={isWidgetOpen} quoteSrc={quoteSrc} />
    </Box>
  );
};

export default BusinessInsuranceLandingPageView;
